import type * as models from 'pkg/api/models';
import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Dateable } from 'pkg/api/models/dateable';
import type { FormField } from 'pkg/api/models/form_field';
import type { FormProductPrice } from 'pkg/api/models/form_product_price';
import type { FormSubmission } from 'pkg/api/models/form_submission';
import type { Record } from 'pkg/api/models/record';

export enum Visibilities {
	Unlisted = 'unlisted',
	Public = 'public',
	Organization = 'organization',
}

export enum Statuses {
	Draft = 'draft',
	Open = 'open',
	Closed = 'closed',
}

export enum FieldTypes {
	ShortText = 'string',
	LongText = 'long_text',
	Number = 'number',
	Email = 'email',
	Phone = 'phone',
	Choice = 'choice',
	MultiChoice = 'multi_choice',
	Date = 'date',
	Country = 'country',
	Section = 'section',
	Attachment = 'attachment',
	Slider = 'slider',
}

export enum FormVersion {
	FormV1 = 1,
	FormV2,
}

/**
 * The registration type an admin picked in the create wizard. `Invalid` is the
 * API's zero value, sent for every form that carries no type: forms that predate
 * types, and forms created without one. It arrives as a real value rather than an
 * absent field, so a set `template` does not mean the form is typed — rule out
 * `Invalid` before treating one as a type an admin chose.
 */
export enum FormTemplate {
	Invalid = 'invalid',
	Standard = 'standard',
	Tryout = 'tryout',
	TeamRegistration = 'team_registration',
	FixedProgram = 'fixed_program',
}

/**
 * How players end up in a team for a team registration. `Invitation` gates the
 * form to players the admin invited from the connected teams, via one package
 * carrying a team invitation rule. `SelfSelection` lets players sign up
 * themselves and pick their team, via one package per team connected through
 * `targetGroupId`. Only meaningful when `template` is `TeamRegistration`.
 */
export enum FormJoinMethod {
	Invitation = 'invitation',
	SelfSelection = 'self_selection',
}

export enum ForceClosedReason {
	Unknown = 'unknown',
	CapacityFull = 'capacity_full',
	ArchivedPrice = 'archived_price',
	ArchivedProduct = 'archived_product',
	InvalidFormFieldKey = 'invalid_form_field_key',
	OptionsCapReached = 'options_cap_reached',
}

export interface Form extends Record, Dateable, AllowedActionable {
	id: number;
	guid?: string;
	groupId: number;
	formCategoryId: number;
	title: string;
	description: string;
	maxSubmissions: number;
	maxSubmissionsPerUser: number;
	isFull: boolean;
	spotsLeft: number;
	allEventGroupsStarted: boolean;
	selectDatesEnabled: boolean;
	attachmentId: number;
	template?: FormTemplate;
	joinMethod?: FormJoinMethod;
	publicUrl?: string;
	publicCategoryUrl?: string;
	submissionCount: number;
	deletedAt: number;
	version: FormVersion;

	parentFormId?: number | null;

	submissionEmailEnabled: boolean;
	submissionEmailSubject?: string;
	submissionEmailContent?: string;
	submissions: FormSubmission[];

	visibility: Visibilities;
	status: Statuses;
	attachment: models.attachment.Attachment;
	forceAuth?: boolean;
	inviteOnly?: boolean;
	hasPayment: boolean;
	requirePayment: boolean;
	forceClosedReason?: ForceClosedReason;
	openSubmissionsAt?: number | null;
	closeSubmissionsAt?: number | null;
	fields: FormField[];
	productPrices: FormProductPrice[];
	childForms?: models.form.Form[];
	parentForm?: models.form.Form;
	packages?: models.formPackage.FormPackage[];
	eventGroups?: models.formEventGroup.FormEventGroup[];
	nonEligibleMessage?: string;
	formCategory: models.formCategory.FormCategory;
	group?: models.group.Group;
	formAgreements?: models.agreement.FormAgreementLink[];
}

export interface FormSummary {
	Settings: {
		TransactionSummary: boolean;
		SubmissionSummary: boolean;
		SubscriptionSummary: boolean;
		OverduePayments: boolean;
		LatestSubmissions: boolean;
	};
	TransactionSummary: {
		Total: number;
		Statuses: {
			Paid: number;
			Open: number;
			PastDue: number;
			Void: number;
			Uncollectible: number;
		};
	};
	SubmissionSummary: {
		Total: number;
		Today: number;
		SevenDays: number;
		ThirtyDays: number;
	};
	SubscriptionSummary: {
		Total: number;
		Statuses: {
			Paid: number;
			Uncollectible: number;
			PastDue: number;
			Canceled: number;
		};
	};
	// Submitted submissions per event group, keyed by FormEventGroup id. Groups
	// nobody picked are absent.
	EventGroupSubmissionCounts: { [eventGroupId: number]: number };

	OverduePayments: models.order.Order[];

	LatestSubmissions: models.formSubmission.FormSubmission[];

	Form: Form;
}

export enum ResponseLimitSource {
	Form = 'form',
	Field = 'field',
}

export interface EffectiveResponseLimit {
	count: number;
	source: ResponseLimitSource;
	/** The question the limit derives from, when `source` is `Field`. */
	fieldLabel?: string;
}
